import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { mappedIcon } from './design16';

interface TemplateModalProps {
  open: boolean;
  /** Announced as the dialog's name, and rendered in the header beside any icon. */
  title: ReactNode;
  /** The line under the title. */
  subtitle?: ReactNode;
  /** A `pi-*` class rendered in the header badge. */
  icon?: string;
  /** Tints the header badge. */
  tone?: 'accent' | 'warning' | 'danger' | 'success';
  /** The reference's own width for this dialog, e.g. 860. */
  width: number;
  /** Footer actions, right-aligned. */
  footer?: ReactNode;
  /** `confirm` sits above `modal`, so a confirmation over a dialog is visible. */
  layer?: 'modal' | 'confirm';
  onClose: () => void;
  children: ReactNode;
  /** design16 kinds: the prototype's Tabler glyph, and no close button where the prototype draws none. */
  d16?: boolean;
  hideClose?: boolean;
  /** A class on the surface, for one dialog's own layout. */
  surfaceClassName?: string;
}

/**
 * The Templates module's own modal.
 *
 * **Why not PrimeReact's `Dialog`.** This application loads `primeicons` and
 * `primeflex` but **no PrimeReact theme** — `primereact/resources/themes/…` is
 * imported nowhere, and only two features import even the core
 * `primereact.min.css`. Without a theme a `p-dialog` has no background, no
 * padding and no elevation, which is exactly the reported symptom: a transparent
 * popup with the table showing through it and fields overlapping the rows
 * beneath.
 *
 * Adding a theme globally would restyle eighteen master screens and the whole
 * Organization Setup wizard, which is not this module's to do. So Templates owns
 * its overlays outright: one component, one backdrop, one stacking context, and
 * every value from `NiraxoBid_Template.html`.
 *
 * Rendered through a **portal to `document.body`**, so no ancestor's `overflow`,
 * `transform` or `z-index` can clip it or trap it below the table — the other
 * half of the same bug.
 */
export function TemplateModal({
  open,
  title,
  subtitle,
  icon,
  tone = 'accent',
  width,
  footer,
  layer = 'modal',
  onClose,
  children,
  d16 = false,
  hideClose = false,
  surfaceClassName,
}: TemplateModalProps) {
  const surface = useRef<HTMLDivElement>(null);

  /* Escape closes, and the page behind does not scroll while a modal is up. */
  useEffect(() => {
    if (!open) return undefined;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose, open]);

  /* Focus lands inside the dialog, not left behind on the row that opened it. */
  useEffect(() => {
    if (open) surface.current?.focus();
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className={`templates-overlay templates-overlay--${layer}`}
      /* A click on the backdrop itself closes; one that started inside does not. */
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={surface}
        className={surfaceClassName ? `templates-overlay__surface ${surfaceClassName}` : 'templates-overlay__surface'}
        style={{ width: `${String(width)}px` }}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
      >
        <header className="templates-overlay__head">
          {icon && (
            <span className={`templates-overlay__badge templates-overlay__badge--${tone}`}>
              <i className={mappedIcon(d16, icon)} aria-hidden="true" />
            </span>
          )}
          <div className="templates-overlay__heading">
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          {hideClose ? null : (
            <button
              type="button"
              className="templates-overlay__close"
              onClick={onClose}
              aria-label="Close dialog"
            >
              <i className={d16 ? 'ti ti-x' : 'pi pi-times'} aria-hidden="true" />
            </button>
          )}
        </header>

        {/* The scroll lives here, so the header and footer stay put. */}
        <div className="templates-overlay__body">{children}</div>

        {footer && <footer className="templates-overlay__foot">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
